import {
  isValidElement,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  InfoBox,
  Modal,
  Spinner,
  Text,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { MdDownload } from "react-icons/md";
import {
  technicalDocsService,
  TechnicalDocDetail,
} from "services/technicalDocsService";

interface TechnicalDocModalProps {
  docId: string | null;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Slug of an `##` / `###` heading to scroll to once the doc has rendered, so a
   * "setup guide" link on a provider's own panel lands on that provider rather
   * than at the top of a long document.
   */
  section?: string | null;
}

/** Flattens a rendered heading back to plain text so it can be slugged. */
const textOf = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean")
    return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement(node)) {
    return textOf((node.props as { children?: ReactNode }).children);
  }
  return "";
};

/** "Help Scout" -> "help-scout". Matches the `section` values panels link with. */
const slug = (node: ReactNode): string =>
  textOf(node)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const sectionMarkdown = (
  markdown: string,
  section: string,
): { heading: string; markdown: string } | null => {
  const lines = markdown.split(/\r?\n/);
  let inCodeFence = false;
  let heading: string | null = null;
  let start = -1;
  let end = lines.length;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trimStart().startsWith("```")) inCodeFence = !inCodeFence;
    const match = inCodeFence ? null : /^##\s+(.+)$/.exec(lines[i]);
    if (!match) continue;
    if (start >= 0) {
      end = i;
      break;
    }
    if (slug(match[1]) === section) {
      heading = match[1].trim();
      start = i;
    }
  }
  if (heading === null) return null;
  return {
    heading,
    markdown: lines
      .slice(start, end)
      .join("\n")
      .replace(/\n-{3,}\s*$/, "")
      .trim(),
  };
};

// Element-level styling for the rendered markdown. Keeps content readable
// without pulling in @tailwindcss/typography. Colours / spacing reference
// existing theme tokens (var(--…)).
const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1
      style={{
        fontSize: "1.5rem",
        fontWeight: 700,
        marginTop: "1.5rem",
        marginBottom: "0.75rem",
        lineHeight: 1.25,
      }}
    >
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2
      id={slug(children)}
      style={{
        fontSize: "1.25rem",
        fontWeight: 700,
        marginTop: "1.25rem",
        marginBottom: "0.5rem",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3
      id={slug(children)}
      style={{
        fontSize: "1.05rem",
        fontWeight: 600,
        marginTop: "1rem",
        marginBottom: "0.4rem",
      }}
    >
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4
      style={{
        fontSize: "0.95rem",
        fontWeight: 600,
        marginTop: "0.85rem",
        marginBottom: "0.3rem",
      }}
    >
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p style={{ marginBottom: "0.75rem", lineHeight: 1.6, fontSize: "0.9rem" }}>
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul
      style={{
        paddingLeft: "1.4rem",
        marginBottom: "0.75rem",
        listStyleType: "disc",
        fontSize: "0.9rem",
      }}
    >
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol
      style={{
        paddingLeft: "1.4rem",
        marginBottom: "0.75rem",
        listStyleType: "decimal",
        fontSize: "0.9rem",
      }}
    >
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: "0.25rem", lineHeight: 1.5 }}>{children}</li>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      style={{ color: "var(--accent-info-fg)", textDecoration: "underline" }}
    >
      {children}
    </a>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    if (isInline) {
      return (
        <code
          style={{
            // Semi-transparent gray reads on both light & dark; currentColor
            // inherits the body text colour so it stays legible in both modes.
            background: "rgba(127, 127, 127, 0.18)",
            color: "currentColor",
            padding: "0.1rem 0.35rem",
            borderRadius: "4px",
            fontSize: "0.85em",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          }}
        >
          {children}
        </code>
      );
    }
    return <code className={className}>{children}</code>;
  },
  pre: ({ children }) => (
    <pre
      style={{
        background: "#0f172a",
        color: "#e2e8f0",
        padding: "0.9rem 1rem",
        borderRadius: "8px",
        overflowX: "auto",
        fontSize: "0.8rem",
        lineHeight: 1.45,
        marginBottom: "0.85rem",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      }}
    >
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div style={{ overflowX: "auto", marginBottom: "0.85rem" }}>
      <table
        style={{
          borderCollapse: "collapse",
          fontSize: "0.85rem",
          width: "100%",
        }}
      >
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th
      style={{
        textAlign: "left",
        padding: "0.45rem 0.65rem",
        // Semi-transparent gray works on both light & dark.
        background: "rgba(127, 127, 127, 0.12)",
        color: "currentColor",
        borderBottom: "1px solid var(--card-border, #e5e7eb)",
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td
      style={{
        padding: "0.4rem 0.65rem",
        borderBottom: "1px solid var(--card-border, #e5e7eb)",
        verticalAlign: "top",
      }}
    >
      {children}
    </td>
  ),
  blockquote: ({ children }) => (
    <blockquote
      style={{
        borderLeft: "3px solid var(--accent-info-rule, #93c5fd)",
        background: "var(--accent-info-wash, #eff6ff)",
        // Pair fg with wash so text stays readable in dark mode.
        color: "var(--accent-info-fg)",
        padding: "0.5rem 0.9rem",
        margin: "0.75rem 0",
        fontSize: "0.88rem",
        borderRadius: "0 6px 6px 0",
      }}
    >
      {children}
    </blockquote>
  ),
  // Step illustrations shipped from the app's own /public/docs/... folder.
  // Bounded so a wide figure cannot force the modal body to scroll sideways.
  img: ({ src, alt }) => (
    <img
      src={typeof src === "string" ? src : undefined}
      alt={alt ?? ""}
      loading="lazy"
      style={{
        display: "block",
        width: "100%",
        height: "auto",
        margin: "1rem 0",
        borderRadius: "8px",
        border: "1px solid var(--card-border, #e5e7eb)",
      }}
    />
  ),
  hr: () => (
    <hr
      style={{
        border: 0,
        borderTop: "1px solid var(--card-border, #e5e7eb)",
        margin: "1.25rem 0",
      }}
    />
  ),
};

export const TechnicalDocModal = ({
  docId,
  isOpen,
  onClose,
  section,
}: TechnicalDocModalProps) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<TechnicalDocDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    setShowFull(false);
  }, [docId, section, isOpen]);

  const focused = useMemo(
    () =>
      detail && section && !showFull
        ? sectionMarkdown(detail.markdown, section)
        : null,
    [detail, section, showFull],
  );

  useEffect(() => {
    if (!isOpen || !docId) {
      setDetail(null);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    technicalDocsService
      .getById(docId)
      .then((d) => setDetail(d))
      .catch(() => setError(t("app.help.technicalDocs.loadError")))
      .finally(() => setIsLoading(false));
  }, [docId, isOpen, t]);

  // Runs after the markdown has been committed to the DOM, so the heading ids
  // from `slug()` already exist. Falls back to the top when the slug is unknown.
  useEffect(() => {
    if (!detail || !section) return;
    const id = window.requestAnimationFrame(() => {
      document
        .getElementById(section)
        ?.scrollIntoView({ block: "start", behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [detail, section, showFull]);

  const handleDownload = () => {
    if (!detail) return;
    const blob = new Blob([detail.markdown], {
      type: "text/markdown;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = detail.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <Flex direction="column" gap={1}>
          <Text fontWeight="semibold" fontSize="lg">
            {detail?.title ?? t("app.help.technicalDocs.modalTitleFallback")}
          </Text>
          {detail && (
            <HStack gap={2}>
              <Badge colorScheme="brand" size="sm">
                {detail.lastUpdated}
              </Badge>
              <Text fontSize="xs" color="gray.500">
                {t("app.help.technicalDocs.lastUpdated")}
              </Text>
            </HStack>
          )}
        </Flex>
      </Modal.Header>

      <Modal.Body maxH="65vh" overflowY="auto">
        {isLoading && (
          <Flex justify="center" py={10}>
            <Spinner />
          </Flex>
        )}
        {error && !isLoading && <InfoBox variant="error">{error}</InfoBox>}
        {detail && !isLoading && !error && (
          <Box className="technical-doc-markdown">
            {focused && (
              <Flex
                align="center"
                justify="space-between"
                gap={3}
                pb={3}
                mb={1}
                borderBottomWidth="1px"
              >
                <Text fontSize="sm" color="gray.500">
                  {t("app.help.technicalDocs.showingSection", {
                    section: focused.heading,
                  })}
                </Text>
                <Button
                  intent="secondary"
                  size="sm"
                  onClick={() => setShowFull(true)}
                >
                  {t("app.help.technicalDocs.showFullGuide")}
                </Button>
              </Flex>
            )}
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {focused?.markdown ?? detail.markdown}
            </ReactMarkdown>
          </Box>
        )}
      </Modal.Body>

      <Modal.Footer>
        <HStack gap={3} justify="flex-end" w="full">
          <Button intent="secondary" onClick={onClose}>
            {t("app.common.close")}
          </Button>
          <Button
            intent="primary"
            onClick={handleDownload}
            disabled={!detail || isLoading}
          >
            <MdDownload />
            {t("app.help.technicalDocs.downloadButton")}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};
